import * as hierarchyFunctions from "contexts/hierarchy/functions";
import * as hierarchyActions from "contexts/hierarchy/interface";
import * as tlfActions from "contexts/tlf/interface";
import * as tlfFunctions from "contexts/tlf/tlf_functions";
import * as tocActions from "contexts/toc/interface";
import * as tocFunctions from "contexts/toc/toc_functions";
import { eHierarchyLevel } from "functions/api/enums";
import * as appFunctions from "./app_functions";
import { finishHistory } from "./finishHistory";
import {
  IUnifiedAction,
  IUnifiedState,
  REDO,
  SAVE_COMPLETED,
  SAVE_TLF_COMPLETED,
  SET_SAVE_REQUEST,
  UNDO,
} from "./interface";
import { app_reduceHierarchySaveBlockers, app_reduceValidationSaveBlockers } from "./saveBlockers";

/** Master and patch editing commands that must never run for a Report's current view. */
const templateEditingOperations: Set<IUnifiedAction["operation"]> = new Set([
  tlfActions.ACTIVATE_VARIANT_PATCH,
  tlfActions.CANCEL_VARIANT_PATCH_CREATION,
  tlfActions.CREATE_VARIANT_PATCH,
  tlfActions.REMOVE_VARIANT_PATCH,
  tlfActions.REMOVE_VARIANT_TEMPLATE,
  tlfActions.RENAME_VARIANT_PATCH,
  tlfActions.RENAME_VARIANT_TEMPLATE,
  tlfActions.SET_CREATE_VARIANT_PATCH_WINDOW,
  tlfActions.SET_VARIANT_CREATION_VIEW,
  tlfActions.SET_VARIANT_PATCH_SELECTION,
  tlfActions.TOGGLE_VARIANT_PATCH,
  tlfActions.TRACK_ACTIVE_VARIANT_PATCH,
  tlfActions.UPSERT_VARIANT_TEMPLATE,
]);

/**
 * Applies a domain action and records only meaningful editable-data changes.
 *
 * @param state - Unified state including independent persisted baselines.
 * @param action - Domain edit, navigation or history command.
 * @returns Unified state with one chronological reversible history.
 */
export const unifiedReducer = (state: IUnifiedState, action: IUnifiedAction): IUnifiedState => {
  const nextState = reduceSlices(state, action);
  if (action.operation === UNDO || action.operation === REDO) {
    return nextState;
  }
  return finishHistory(state, nextState, action);
};

/**
 * Unified reducer for application, hierarchy, TLF, and ToC actions.
 *
 * @param state - Current unified application state
 * @param action - The unified action to apply.
 * @returns The updated {@link IUnifiedState}.
 */
const reduceSlices = (state: IUnifiedState, action: IUnifiedAction): IUnifiedState => {
  if (
    state.hierarchyState.hierarchy?.level === eHierarchyLevel.Report &&
    templateEditingOperations.has(action.operation)
  )
    return state;
  switch (action.operation) {
    // Added to ensure that undo and redo operations are included in the slice
    case REDO:
      return appFunctions.app_undoRedo(state, action);
    case UNDO:
      return appFunctions.app_undoRedo(state, action);
    case hierarchyActions.ADD_DOMAIN_REPLACEMENT: {
      const hierarchyState = hierarchyFunctions.hcr_addDomainReplacement(
        state.hierarchyState,
        action,
      );
      return {
        ...state,
        hierarchyState,
        saveBlockers: app_reduceHierarchySaveBlockers(
          state.saveBlockers,
          hierarchyState,
          action,
          state.allowedReportTypes,
        ),
      };
    }
    case hierarchyActions.ADD_HIERARCHY_NODES: {
      const hierarchyState = hierarchyFunctions.hcr_addHierarchyNodes(state.hierarchyState, action);
      return {
        ...state,
        hierarchyState,
        saveBlockers: app_reduceHierarchySaveBlockers(
          state.saveBlockers,
          hierarchyState,
          action,
          state.allowedReportTypes,
        ),
      };
    }
    case hierarchyActions.ADD_SINGLE_REPLACEMENT: {
      const hierarchyState = hierarchyFunctions.hcr_addSingleReplacement(
        state.hierarchyState,
        action,
      );
      return {
        ...state,
        hierarchyState,
        saveBlockers: app_reduceHierarchySaveBlockers(
          state.saveBlockers,
          hierarchyState,
          action,
          state.allowedReportTypes,
        ),
      };
    }
    case hierarchyActions.CLEAR_HIERARCHY: {
      const hierarchyState = hierarchyFunctions.hcr_clearHierarchy(state.hierarchyState);
      return {
        ...state,
        hierarchyState,
        saveBlockers: app_reduceHierarchySaveBlockers(
          state.saveBlockers,
          hierarchyState,
          action,
          state.allowedReportTypes,
        ),
      };
    }
    case hierarchyActions.LOAD_CLINICAL_LIBRARY: {
      const hierarchyState = hierarchyFunctions.hcr_loadClinicalLibrary(
        state.hierarchyState,
        action,
      );
      return {
        ...state,
        hierarchyState,
        saveBlockers: app_reduceHierarchySaveBlockers(
          state.saveBlockers,
          hierarchyState,
          action,
          state.allowedReportTypes,
        ),
      };
    }
    case hierarchyActions.LOAD_HIERARCHY: {
      const hierarchyState = hierarchyFunctions.hcr_loadHierarchy(state.hierarchyState, action);
      return {
        ...state,
        hierarchyState,
        saveBlockers: app_reduceHierarchySaveBlockers(
          state.saveBlockers,
          hierarchyState,
          action,
          state.allowedReportTypes,
        ),
      };
    }
    case hierarchyActions.PATCH_HIERARCHY_COMMENTS: {
      const hierarchyState = hierarchyFunctions.hcr_patchHierarchyComments(
        state.hierarchyState,
        action,
      );
      return {
        ...state,
        hierarchyState,
        saveBlockers: app_reduceHierarchySaveBlockers(
          state.saveBlockers,
          hierarchyState,
          action,
          state.allowedReportTypes,
        ),
      };
    }
    case hierarchyActions.REMOVE_DOMAIN_REPLACEMENT: {
      const hierarchyState = hierarchyFunctions.hcr_removeDomainReplacement(
        state.hierarchyState,
        action,
      );
      return {
        ...state,
        hierarchyState,
        saveBlockers: app_reduceHierarchySaveBlockers(
          state.saveBlockers,
          hierarchyState,
          action,
          state.allowedReportTypes,
        ),
      };
    }
    case hierarchyActions.REMOVE_HIERARCHY_COMMENT: {
      const hierarchyState = hierarchyFunctions.hcr_removeHierarchyComment(
        state.hierarchyState,
        action,
      );
      return {
        ...state,
        hierarchyState,
        saveBlockers: app_reduceHierarchySaveBlockers(
          state.saveBlockers,
          hierarchyState,
          action,
          state.allowedReportTypes,
        ),
      };
    }
    case hierarchyActions.REMOVE_HIERARCHY_NODE: {
      const hierarchyState = hierarchyFunctions.hcr_removeHierarchyNode(
        state.hierarchyState,
        action,
      );
      return {
        ...state,
        hierarchyState,
        saveBlockers: app_reduceHierarchySaveBlockers(
          state.saveBlockers,
          hierarchyState,
          action,
          state.allowedReportTypes,
        ),
      };
    }
    case hierarchyActions.REMOVE_SINGLE_REPLACEMENT: {
      const hierarchyState = hierarchyFunctions.hcr_removeSingleReplacement(
        state.hierarchyState,
        action,
      );
      return {
        ...state,
        hierarchyState,
        saveBlockers: app_reduceHierarchySaveBlockers(
          state.saveBlockers,
          hierarchyState,
          action,
          state.allowedReportTypes,
        ),
      };
    }
    case SAVE_COMPLETED:
      return appFunctions.app_saveCompleted(state, action);
    case hierarchyActions.SAVE_HIERARCHY_COMPLETED: {
      const hierarchyState = hierarchyFunctions.hcr_saveHierarchyCompleted(
        state.hierarchyState,
        action,
      );
      return {
        ...state,
        hierarchyState,
        saveBlockers: app_reduceHierarchySaveBlockers(
          state.saveBlockers,
          hierarchyState,
          action,
          state.allowedReportTypes,
        ),
      };
    }
    case hierarchyActions.SAVE_HIERARCHY_FAILED: {
      const hierarchyState = hierarchyFunctions.hcr_saveHierarchyFailed(state.hierarchyState);
      return {
        ...state,
        hierarchyState,
        saveBlockers: app_reduceHierarchySaveBlockers(
          state.saveBlockers,
          hierarchyState,
          action,
          state.allowedReportTypes,
        ),
      };
    }
    case hierarchyActions.SAVE_TOC_MAJOR_VERSION: {
      const hierarchyState = hierarchyFunctions.hcr_saveTocMajorVersion(
        state.hierarchyState,
        action,
      );
      return {
        ...state,
        hierarchyState,
        saveBlockers: app_reduceHierarchySaveBlockers(
          state.saveBlockers,
          hierarchyState,
          action,
          state.allowedReportTypes,
        ),
      };
    }
    case SAVE_TLF_COMPLETED:
      return appFunctions.app_saveTlfCompleted(state, action);
    case SET_SAVE_REQUEST:
      return appFunctions.app_setSaveRequest(state, action);
    case hierarchyActions.SET_ACCESS_RIGHTS: {
      const hierarchyState = hierarchyFunctions.hcr_setAccessRights(state.hierarchyState, action);
      return {
        ...state,
        hierarchyState,
        saveBlockers: app_reduceHierarchySaveBlockers(
          state.saveBlockers,
          hierarchyState,
          action,
          state.allowedReportTypes,
        ),
      };
    }
    case hierarchyActions.SET_CLINICAL_LIBRARY_LOADING: {
      const hierarchyState = hierarchyFunctions.hcr_setClinicalLibraryLoading(
        state.hierarchyState,
        action,
      );
      return {
        ...state,
        hierarchyState,
        saveBlockers: app_reduceHierarchySaveBlockers(
          state.saveBlockers,
          hierarchyState,
          action,
          state.allowedReportTypes,
        ),
      };
    }
    case hierarchyActions.SET_HIERARCHY_COMMENT_PANEL: {
      const hierarchyState = hierarchyFunctions.hcr_setHierarchyCommentPanel(
        state.hierarchyState,
        action,
      );
      return {
        ...state,
        hierarchyState,
        saveBlockers: app_reduceHierarchySaveBlockers(
          state.saveBlockers,
          hierarchyState,
          action,
          state.allowedReportTypes,
        ),
      };
    }
    case hierarchyActions.SET_HIERARCHY_ID: {
      const hierarchyState = hierarchyFunctions.hcr_setHierarchyId(state.hierarchyState, action);
      return {
        ...state,
        hierarchyState,
        saveBlockers: app_reduceHierarchySaveBlockers(
          state.saveBlockers,
          hierarchyState,
          action,
          state.allowedReportTypes,
        ),
      };
    }
    case hierarchyActions.SET_HIERARCHY_SAVE_REQUEST_STATUS: {
      const hierarchyState = hierarchyFunctions.hcr_setHierarchySaveRequestStatus(
        state.hierarchyState,
        action,
      );
      return {
        ...state,
        hierarchyState,
        saveBlockers: app_reduceHierarchySaveBlockers(
          state.saveBlockers,
          hierarchyState,
          action,
          state.allowedReportTypes,
        ),
      };
    }
    case hierarchyActions.SET_HIERARCHY_SAVE_TOC_REQUEST_STATUS: {
      const hierarchyState = hierarchyFunctions.hcr_setHierarchySaveTocRequestStatus(
        state.hierarchyState,
        action,
      );
      return {
        ...state,
        hierarchyState,
        saveBlockers: app_reduceHierarchySaveBlockers(
          state.saveBlockers,
          hierarchyState,
          action,
          state.allowedReportTypes,
        ),
      };
    }
    case hierarchyActions.SET_HIERARCHY_SUBSCRIPTION_STATUS: {
      const hierarchyState = hierarchyFunctions.hcr_setHierarchySubscriptionStatus(
        state.hierarchyState,
        action,
      );
      return {
        ...state,
        hierarchyState,
        saveBlockers: app_reduceHierarchySaveBlockers(
          state.saveBlockers,
          hierarchyState,
          action,
          state.allowedReportTypes,
        ),
      };
    }
    case hierarchyActions.SET_HIERARCHY_VALIDATION_SAVE_BLOCKER:
      return {
        ...state,
        saveBlockers: app_reduceValidationSaveBlockers(state.saveBlockers, action),
      };
    case hierarchyActions.UPDATE_DOMAIN_REPLACEMENT: {
      const hierarchyState = hierarchyFunctions.hcr_updateDomainReplacement(
        state.hierarchyState,
        action,
      );
      return {
        ...state,
        hierarchyState,
        saveBlockers: app_reduceHierarchySaveBlockers(
          state.saveBlockers,
          hierarchyState,
          action,
          state.allowedReportTypes,
        ),
      };
    }
    case hierarchyActions.UPDATE_HIERARCHY: {
      const hierarchyState = hierarchyFunctions.hcr_updateHierarchy(state.hierarchyState, action);
      return {
        ...state,
        hierarchyState,
        saveBlockers: app_reduceHierarchySaveBlockers(
          state.saveBlockers,
          hierarchyState,
          action,
          state.allowedReportTypes,
        ),
      };
    }
    case hierarchyActions.UPDATE_HIERARCHY_COMMENT: {
      const hierarchyState = hierarchyFunctions.hcr_updateHierarchyComment(
        state.hierarchyState,
        action,
      );
      return {
        ...state,
        hierarchyState,
        saveBlockers: app_reduceHierarchySaveBlockers(
          state.saveBlockers,
          hierarchyState,
          action,
          state.allowedReportTypes,
        ),
      };
    }
    case hierarchyActions.UPDATE_SINGLE_REPLACEMENT: {
      const hierarchyState = hierarchyFunctions.hcr_updateSingleReplacements(
        state.hierarchyState,
        action,
      );
      return {
        ...state,
        hierarchyState,
        saveBlockers: app_reduceHierarchySaveBlockers(
          state.saveBlockers,
          hierarchyState,
          action,
          state.allowedReportTypes,
        ),
      };
    }
    case hierarchyActions.UPDATE_TLF_ITEM_INDEX: {
      const hierarchyState = hierarchyFunctions.hcr_updateTlfItemIndex(
        state.hierarchyState,
        action,
      );
      return {
        ...state,
        hierarchyState,
        saveBlockers: app_reduceHierarchySaveBlockers(
          state.saveBlockers,
          hierarchyState,
          action,
          state.allowedReportTypes,
        ),
      };
    }
    case tlfActions.ACTIVATE_VARIANT_PATCH:
      return {
        ...state,
        tlfState: tlfFunctions.tlf_activateVariantPatch(state.tlfState, action),
      };
    case tlfActions.ADD_RECEIVED_MESSAGE:
      return { ...state, tlfState: tlfFunctions.tlf_addReceivedMessage(state.tlfState, action) };
    case tlfActions.APPLY_VARIANT_TEMPLATE:
      return { ...state, tlfState: tlfFunctions.tlf_applyVariantTemplate(state.tlfState, action) };
    case tlfActions.CANCEL_VARIANT_PATCH_CREATION:
      return { ...state, tlfState: tlfFunctions.tlf_cancelVariantPatchCreation(state.tlfState) };
    case tlfActions.CLEAR_TLF:
      return { ...state, tlfState: tlfFunctions.tlf_clearTlf() };
    case tlfActions.CLOSE_MODAL_WINDOW:
      return { ...state, tlfState: tlfFunctions.tlf_closeModalWindow(state.tlfState) };
    case tlfActions.CREATE_VARIANT_PATCH:
      return { ...state, tlfState: tlfFunctions.tlf_createVariantPatch(state.tlfState, action) };
    case tlfActions.LOAD_TLF:
      return { ...state, tlfState: tlfFunctions.tlf_loadTlf(state.tlfState, action) };
    case tlfActions.PATCH_TLF_COMMENTS:
      return { ...state, tlfState: tlfFunctions.tlf_patchTlfComments(state.tlfState, action) };
    case tlfActions.PROCESS_SIGNALR_NOTIFICATION:
      return {
        ...state,
        tlfState: tlfFunctions.tlf_processSignalrNotification(state.tlfState, action),
      };
    case tlfActions.REMOVE_TLF_COMMENT:
      return { ...state, tlfState: tlfFunctions.tlf_removeTlfComment(state.tlfState, action) };
    case tlfActions.REMOVE_VARIANT_PATCH:
      return { ...state, tlfState: tlfFunctions.tlf_removeVariantPatch(state.tlfState, action) };
    case tlfActions.REMOVE_VARIANT_TEMPLATE:
      return { ...state, tlfState: tlfFunctions.tlf_removeVariantTemplate(state.tlfState, action) };
    case tlfActions.RENAME_VARIANT_PATCH:
      return { ...state, tlfState: tlfFunctions.tlf_renameVariantPatch(state.tlfState, action) };
    case tlfActions.RENAME_VARIANT_TEMPLATE:
      return { ...state, tlfState: tlfFunctions.tlf_renameVariantTemplate(state.tlfState, action) };
    case tlfActions.REVERT_TO_VARIANT_MASTER:
      return {
        ...state,
        tlfState: tlfFunctions.tlf_revertToVariantMaster(state.tlfState, action),
      };
    case tlfActions.SET_CREATE_VARIANT_PATCH_WINDOW:
      return {
        ...state,
        tlfState: tlfFunctions.tlf_setCreateVariantPatchWindow(state.tlfState, action),
      };
    case tlfActions.SET_EDITOR_BLOCKS_SEARCH_PANEL:
      return {
        ...state,
        tlfState: tlfFunctions.tlf_setEditorBlocksSearchPanel(state.tlfState, action),
      };
    case tlfActions.SET_TLF_COMMENT_PANEL:
      return { ...state, tlfState: tlfFunctions.tlf_setTlfCommentPanel(state.tlfState, action) };
    case tlfActions.SET_TLF_READ_ONLY:
      return { ...state, tlfState: tlfFunctions.tlf_setTlfReadOnly(state.tlfState, action) };
    case tlfActions.SET_TLF_SUBSCRIPTION_STATUS:
      return {
        ...state,
        tlfState: tlfFunctions.tlf_setTlfSubscriptionStatus(state.tlfState, action),
      };
    case tlfActions.SET_VARIANT_CREATION_VIEW:
      return {
        ...state,
        tlfState: tlfFunctions.tlf_setVariantCreationMode(state.tlfState, action),
      };
    case tlfActions.SET_VARIANT_PATCH_SELECTION:
      return {
        ...state,
        tlfState: tlfFunctions.tlf_setVariantPatchSelection(state.tlfState, action),
      };
    case tlfActions.SET_VIEW_MODE:
      return { ...state, tlfState: tlfFunctions.tlf_setViewMode(state.tlfState, action) };
    case tlfActions.SHOW_MODAL_WINDOW:
      return { ...state, tlfState: tlfFunctions.tlf_showModalWindow(state.tlfState, action) };
    case tlfActions.TOGGLE_TRAFFIC_LIGHTS:
      return { ...state, tlfState: tlfFunctions.tlf_toggleTrafficLights(state.tlfState, action) };
    case tlfActions.TOGGLE_VARIANT_PATCH:
      return {
        ...state,
        tlfState: tlfFunctions.tlf_toggleVariantPatch(state.tlfState, action),
      };
    case tlfActions.TRACK_ACTIVE_VARIANT_PATCH:
      return {
        ...state,
        tlfState: tlfFunctions.tlf_trackActiveVariantPatch(state.tlfState, action),
      };
    case tlfActions.UPDATE_TLF: {
      let tlfState = tlfFunctions.tlf_updateTlf(state.tlfState, action);
      if (
        tlfState.activeVariantPatchName !== null &&
        !action.newSavePoint &&
        !action.doNotAddUndo &&
        !action.reset &&
        !action.calledFrom.startsWith("SignalR")
      ) {
        // Record the edited view and its derived patch catalogue as one global undo entry.
        tlfState = tlfFunctions.tlf_trackActiveVariantPatch(tlfState, {
          operation: tlfActions.TRACK_ACTIVE_VARIANT_PATCH,
          calledFrom: action.calledFrom,
        });
      }
      return { ...state, tlfState };
    }
    case tlfActions.UPDATE_TLF_COMMENT:
      return { ...state, tlfState: tlfFunctions.tlf_updateTlfComment(state.tlfState, action) };
    case tlfActions.UPSERT_VARIANT_TEMPLATE:
      return { ...state, tlfState: tlfFunctions.tlf_upsertVariantTemplate(state.tlfState, action) };
    case tocActions.ADD_NEW_TOC_ENTRIES:
      return { ...state, tocState: tocFunctions.toc_addNewTocEntries(state.tocState, action) };
    case tocActions.ADD_SECTION:
      return { ...state, tocState: tocFunctions.toc_addSection(state.tocState, action) };
    case tocActions.CLEAR_REMOVED:
      return { ...state, tocState: tocFunctions.toc_clearRemoved(state.tocState) };
    case tocActions.CLOSE_TOC_NEED_REFRESH:
      return { ...state, tocState: tocFunctions.toc_closeTocNeedRefresh(state.tocState) };
    case tocActions.CLOSE_TOC_SAVE_ALERT:
      return { ...state, tocState: tocFunctions.toc_closeTocSaveAlert(state.tocState) };
    case tocActions.DELETE:
      return { ...state, tocState: tocFunctions.toc_delete(state.tocState, action) };
    case tocActions.FROM_SIGNALR:
      return { ...state, tocState: tocFunctions.toc_fromSignalR(state.tocState, action) };
    case tocActions.MOVE_ITEM:
      return { ...state, tocState: tocFunctions.toc_moveItem(state.tocState, action) };
    case tocActions.REASSIGN_TOC_NUMBERS:
      return { ...state, tocState: tocFunctions.toc_reassignTocNumbers(state.tocState) };
    case tocActions.REFRESH:
      return { ...state, tocState: tocFunctions.toc_refresh(state.tocState, action) };
    case tocActions.SAVE_FAIL:
      return { ...state, tocState: tocFunctions.toc_saveFail(state.tocState) };
    case tocActions.SAVE_SUCCESS:
      return { ...state, tocState: tocFunctions.toc_saveSuccess(state.tocState, action) };
    case tocActions.SET_TOC_REQUEST_STATUS:
      return { ...state, tocState: tocFunctions.toc_setTocRequestStatus(state.tocState, action) };
    case tocActions.SET_TYPE_FILTER:
      return { ...state, tocState: tocFunctions.toc_setTypeFilter(state.tocState, action) };
    case tocActions.SHOW_TOC_NEED_REFRESH:
      return { ...state, tocState: tocFunctions.toc_showTocNeedRefresh(state.tocState) };
    case tocActions.SHOW_TOC_SAVE_ALERT:
      return { ...state, tocState: tocFunctions.toc_showTocSaveAlert(state.tocState, action) };
    case tocActions.SORT_TOC:
      return { ...state, tocState: tocFunctions.toc_sortToc(state.tocState) };
    case tocActions.UPDATE_AFTER_PATCH:
      return { ...state, tocState: tocFunctions.toc_updateAfterPatch(state.tocState) };
    case tocActions.UPDATE_COMMENTS_FILTER:
      return { ...state, tocState: tocFunctions.toc_updateCommentsFilter(state.tocState, action) };
    case tocActions.UPDATE_ENTRY:
      return { ...state, tocState: tocFunctions.toc_updateEntry(state.tocState, action) };
    case tocActions.UPDATE_LAST_MAJOR_VERSION_SAVED_AT:
      return {
        ...state,
        tocState: tocFunctions.toc_updateLastMajorVersionSavedAt(state.tocState, action),
      };
    case tocActions.UPDATE_SEARCH_TEXT:
      return { ...state, tocState: tocFunctions.toc_updateSearchText(state.tocState, action) };
    case tocActions.UPDATE_SECTION:
      return { ...state, tocState: tocFunctions.toc_updateSection(state.tocState, action) };
    case tocActions.UPDATE_TRAFFIC_LIGHTS_FILTER:
      return {
        ...state,
        tocState: tocFunctions.toc_updateTrafficLightsFilter(state.tocState, action),
      };
    case tocActions.UPDATE_VIEW_TIFO:
      return { ...state, tocState: tocFunctions.toc_updateViewTiFo(state.tocState, action) };
  }
  // There should never be a final return statement - all possible outcomes should have a corresponding case above.
};
